import {
  UIElementDataProvider,
  SupabaseUIElementType,
  SupabaseUIElementInstance,
  type UIElementDeletion
} from '@/modules/uiElements/types';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import {
  uiElementTypeToLegacy,
  uiElementTypeCreateBody,
  uiElementTypePatchBody,
  type LegacyUiElementType,
  type UiElementTypeDto,
} from '@/infrastructure/api/v1/mappers/uiElementTypes';
import {
  uiElementInstanceToLegacy,
  uiElementInstanceCreateBody,
  uiElementInstancePatchBody,
  type LegacyUiElementInstance,
  type UiElementInstanceDto,
} from '@/infrastructure/api/v1/mappers/uiElementInstances';
import { getOrgMemberId } from './orgMemberIdCache';

import type { WriteScope } from '@/modules/undoRedo';
const typeRow = (dto: UiElementTypeDto) => uiElementTypeToLegacy(dto) as unknown as SupabaseUIElementType;
const instanceRow = (dto: UiElementInstanceDto) => uiElementInstanceToLegacy(dto) as unknown as SupabaseUIElementInstance;

export class UIElementAdapter implements UIElementDataProvider {
  // orgMemberId is always the signed-in user's membership, so their own styles are listed
  async fetchTypesByOrgMember(_orgMemberId: number): Promise<SupabaseUIElementType[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/ui-element-types', {
        params: { query: { owner: 'me', isActive: true, ...page } },
      }));

      if (error) throw error;
      return data.map(typeRow);
    } catch (error) {
      console.error('Error fetching UI element types:', error);
      throw error;
    }
  }

  async fetchTypeByTypeId(typeId: string): Promise<SupabaseUIElementType | null> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/ui-element-types/{typeUniqueId}', { params: { path: { typeUniqueId: typeId } } }));

      if (error) {
        if (error.status === 404) return null;
        throw error;
      }
      return typeRow(data);
    } catch (error) {
      console.error('Error fetching UI element type by ID:', error);
      throw error;
    }
  }

  async saveType(typeData: SupabaseUIElementType): Promise<SupabaseUIElementType> {
    try {
      const { data, error } = await toResult(v1.POST('/api/v1/ui-element-types', {
        body: uiElementTypeCreateBody(typeData as unknown as Partial<LegacyUiElementType>),
      }));

      if (error) throw error;
      return typeRow(data);
    } catch (error) {
      console.error('Error saving UI element type:', error);
      throw error;
    }
  }

  async upsertType(typeData: SupabaseUIElementType, scope?: WriteScope): Promise<void> {
    try {
      // The owner, id and creation time are fixed once the style exists, so only the rest is changed
      const changes: Partial<SupabaseUIElementType> = { ...typeData };
      delete changes.type_id;
      delete changes.org_member_id;
      delete changes.created_at;
      const updated = await toResult(v1.PATCH('/api/v1/ui-element-types/{typeUniqueId}', {
        params: { path: { typeUniqueId: typeData.type_id } },
        body: uiElementTypePatchBody(changes as unknown as Partial<LegacyUiElementType>),
        headers: scope?.headers,
      }));
      if (updated.error?.status !== 404) {
        if (updated.error) throw updated.error;
        return;
      }

      const { error } = await toResult(v1.POST('/api/v1/ui-element-types', {
        body: uiElementTypeCreateBody(typeData as unknown as Partial<LegacyUiElementType>),
      }));
      if (error) throw error;
    } catch (error) {
      console.error('Error upserting UI element type:', error);
      throw error;
    }
  }

  async fetchOrgMemberIdByUserId(userId: string): Promise<number | null> {
    return getOrgMemberId(userId);
  }

  async fetchInstancesByScene(sceneInstanceId: string): Promise<SupabaseUIElementInstance[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/ui-element-instances', {
        params: { query: { sceneUniqueId: [sceneInstanceId], isActive: true, ...page } },
      }));

      if (error) throw error;
      return data.map(instanceRow);
    } catch (error) {
      console.error('Error fetching UI element instances:', error);
      throw error;
    }
  }

  async addInstance(instance: SupabaseUIElementInstance, scope?: WriteScope): Promise<SupabaseUIElementInstance> {
    try {
      // The author is the caller's membership, set by the server; the row keeps the one it was built with
      const { org_member_id: orgMemberId, ...row } = instance;
      const { data, error } = await toResult(v1.POST('/api/v1/ui-element-instances', {
        body: uiElementInstanceCreateBody(row as unknown as Partial<LegacyUiElementInstance>),
        headers: scope?.headers,
      }));

      if (error) throw error;
      return { ...instanceRow(data), org_member_id: orgMemberId };
    } catch (error) {
      console.error('Error adding UI element instance:', error);
      throw error;
    }
  }

  async updateInstance(
    instanceId: string,
    updates: Partial<SupabaseUIElementInstance>,
    scope?: WriteScope
  ): Promise<SupabaseUIElementInstance> {
    try {
      const { data, error } = await toResult(v1.PATCH('/api/v1/ui-element-instances/{uniqueId}', {
        params: { path: { uniqueId: instanceId } },
        body: uiElementInstancePatchBody(updates as unknown as Partial<LegacyUiElementInstance>),
        headers: scope?.headers,
      }));

      if (error) throw error;
      return instanceRow(data);
    } catch (error) {
      console.error('Error updating UI element instance:', error);
      throw error;
    }
  }

  async deleteInstance(instanceId: string, scope?: WriteScope): Promise<UIElementDeletion | null> {
    try {
      const { data, error } = await toResult(v1.DELETE('/api/v1/ui-element-instances/{uniqueId}', { params: { path: { uniqueId: instanceId } }, headers: scope?.headers }));

      // An element already gone is deleted, as the generic route's filtered delete treated it
      if (error?.status === 404) return null;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error deleting UI element instance:', error);
      throw error;
    }
  }
}
